import {
  Button,
  Input,
  InputType,
  Typography,
} from '../../components/primitives';
import { ExpiredRegistrationStatus } from '../../components/text/ExpiredRegistrationStatus';
import {
  TermsAndConditions,
  TermsAndConditionsEnum,
} from '../../components/text/TermsAndConditions';
import Alert from '../../components/ui/Alert';
import { RequiredFieldIndicator } from '../../components/ui/RequiredFieldIndicator';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../components/ui/form';
import { Environment } from '../../env/Environment';
import {
  urlParamMessageTypeEnum,
  useUrlParamsMessage,
} from '../../hooks/urlParamsMessageHook';
import { Icons } from '../../icons';
import { SearchParamsBuilder, testId, toParams } from '../../utils';
import { storeTokensV2 } from '../../utils/auth/session-storage';
import {
  PASSWORD_MAX,
  PASSWORD_MIN,
  TEMP_OIDC_REDIRECT,
  USERNAME_OR_PASSWORD_MIN,
} from '../../utils/constants';
import { PageCard } from './PageCard';
import { zodResolver } from '@hookform/resolvers/zod';
import { Cornerstone } from '@rpi/api';
import { Trans, useTranslation } from '@rpi/i18n';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

export const LogInRoute: React.FC = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route.auth' });

  const [searchParams] = useSearchParams();

  const { urlParamMessage, clearUrlParamMessage } = useUrlParamsMessage();

  const LogInSchema = z
    .object({
      usernameOrEmail: z
        .string()
        .min(
          USERNAME_OR_PASSWORD_MIN,
          `${t('email_or_username_requirements')}`,
        ),
      password: z
        .string()
        .min(PASSWORD_MIN, `${t('minPassword', { minChar: PASSWORD_MIN })}`),
    })
    .required();

  type LogInSchema = z.infer<typeof LogInSchema>;

  const form = useForm<LogInSchema>({
    resolver: zodResolver(LogInSchema),
    defaultValues: {
      usernameOrEmail: searchParams.get('usernameOrEmail') ?? '',
      password: '',
    },
    mode: 'onBlur',
  });

  const { isError, error, isLoading, mutate } = Cornerstone.useLoginV2({
    mutation: {
      onSuccess(response) {
        const data = response.data;

        storeTokensV2(data.accessToken, data.refreshToken);
        const accessToken = data.accessToken;
        const params = new URLSearchParams(searchParams);
        params.set('code', accessToken);
        window.location.href = `${TEMP_OIDC_REDIRECT}?${params.toString()}`;
      },
      onError() {
        clearUrlParamMessage();
      },
    },
  });

  function onSubmit(data: LogInSchema) {
    mutate({
      data: { usernameOrEmail: data.usernameOrEmail, password: data.password },
    });
  }

  const mutationData = error?.response?.data as any;

  const alertText = useMemo(() => {
    if (urlParamMessage?.message === urlParamMessageTypeEnum.password_reset)
      return t('password_reset_success');
    if (
      urlParamMessage?.message === urlParamMessageTypeEnum.registration_success
    )
      return t('registration_success');
    if (
      mutationData?.errorCode ===
      Cornerstone.ErrorCode.MISSING_2_STEP_REGISTRATION_CONFIRMATION
    ) {
      return (
        <ExpiredRegistrationStatus email={form.getValues().usernameOrEmail} />
      );
    }
    if (mutationData?.errorCode === Cornerstone.ErrorCode.BAD_CREDENTIALS) {
      return t('login_error');
    }
    if (isError) return t('generic_error');
    return null;
  }, [urlParamMessage, isError, mutationData, t, form]);

  return (
    <PageCard>
      <Form {...form}>
        <form
          noValidate
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-6"
        >
          {alertText && (
            <Alert
              data-testid="signin-alert"
              variant={
                error ||
                mutationData?.errorCode ===
                  Cornerstone.ErrorCode.MISSING_2_STEP_REGISTRATION_CONFIRMATION
                  ? 'error'
                  : 'success'
              }
            >
              {alertText}
            </Alert>
          )}
          <Typography variant="title-default" as="h1" className="text-center">
            {t('login_title')}
          </Typography>
          <RequiredFieldIndicator />
          <FormField
            control={form.control}
            name="usernameOrEmail"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel {...testId('usernameOrEmail', 'label')}>
                  <Typography variant="form-label">
                    <Trans
                      i18nKey="route.auth.username_or_email"
                      components={{
                        'required-indicator': <Icons.Indicator />,
                      }}
                    />
                  </Typography>
                </FormLabel>
                <FormControl>
                  <Input
                    {...testId('usernameOrEmail')}
                    type={InputType.EMAIL}
                    placeholder=""
                    {...field}
                    invalid={fieldState.invalid}
                  />
                </FormControl>
                <FormMessage {...testId('usernameOrEmail', 'message')} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel {...testId('password', 'label')}>
                  <Typography variant="form-label">
                    <Trans
                      i18nKey="route.auth.password"
                      components={{
                        'required-indicator': <Icons.Indicator />,
                      }}
                    />
                  </Typography>
                </FormLabel>
                <FormControl>
                  <Input
                    {...testId('password')}
                    type={InputType.PASSWORD}
                    placeholder=""
                    {...field}
                    maxLength={PASSWORD_MAX}
                    invalid={fieldState.invalid}
                  />
                </FormControl>
                <FormMessage {...testId('password', 'message')} />
              </FormItem>
            )}
          />
          <Typography variant="body">
            <Link
              to={Environment.LINK.BLURBY_FORGOT_PASSWORD}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('forgot_password_link')}
            </Link>
          </Typography>

          <TermsAndConditions termsEnum={TermsAndConditionsEnum.SIGNIN} />
          <Button
            {...testId('submit')}
            type="submit"
            className="w-full"
            loading={isLoading}
          >
            {t('login')}
          </Button>
          <Typography variant="body" className="text-center">
            <Trans
              i18nKey="route.auth.login_need_account"
              components={{
                'signup-link': (
                  <Link
                    to={toParams(
                      '/v2/auth/register',
                      new SearchParamsBuilder(searchParams).add(
                        'usernameOrEmail',
                        form.getValues().usernameOrEmail,
                      ),
                    )}
                  />
                ),
              }}
            />
          </Typography>
        </form>
      </Form>
    </PageCard>
  );
};
